/**
 * Utility locates on an earthworks site's own page (fieldiq-api#1284).
 *
 * A locate belongs to the SITE and may be narrowed to one of its jobs. Its
 * status (valid / not yet valid / expired) is the API's, on the tenant's
 * today. Adding is dispatch roles. The locate sheet is an `ew_site` upload
 * filed against THIS site, so it can be attached before any job is booked.
 */
import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadEwFile, useCreateEwLocate, useDeleteEwLocate, useEwLocates } from '../../hooks/useEwField';
import { blankToNull, ewDay, ewLocateSkin, ewRefusal } from '../../lib/earthworks';
import type { EwLocate, EwSiteJob } from '../../types/earthworks';
import { EW_DATE_INPUT, EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwBadge, EwDialog, EwEmpty, EwField, EwFormError, EwPanel, EwRegister, type EwColumn } from './EwBits';
import { FileLink, writeError } from './JobFieldSections';

const EMPTY = { ticket_number: '', provider: '', requested_on: '', valid_from: '', valid_to: '', job_id: '', notes: '' };

export function LocatesSection({ siteId, jobs, canWrite }: { siteId: string; jobs: EwSiteJob[]; canWrite: boolean }) {
  const q = useEwLocates(siteId);
  const add = useCreateEwLocate();
  const remove = useDeleteEwLocate();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<EwLocate | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const rows = q.data?.data ?? [];

  const columns: EwColumn<EwLocate>[] = [
    { key: 'ticket', header: 'Ticket #', render: (l) => <span className="font-mono font-extrabold text-white">{l.ticketNumber || '—'}</span> },
    { key: 'provider', header: 'Provider', render: (l) => l.provider || '—' },
    { key: 'valid', header: 'Valid', render: (l) => `${ewDay(l.validFrom)} → ${ewDay(l.validTo)}` },
    { key: 'status', header: 'Status', render: (l) => <EwBadge skin={ewLocateSkin(l.status)} value={l.status} /> },
    { key: 'job', header: 'Job', render: (l) => <span className="font-mono">{l.jobNumber || 'Whole site'}</span> },
    { key: 'file', header: 'Sheet', render: (l) => <FileLink file={l.file} /> },
    ...(canWrite
      ? [{
          key: 'remove',
          header: '',
          align: 'right' as const,
          render: (l: EwLocate) => (
            <button type="button" className={EW_SECONDARY} aria-label={`Remove locate ${l.ticketNumber}`} onClick={() => { setRemoveError(null); setRemoving(l); }}>
              <Trash2 className="size-4" aria-hidden="true" />
              Remove
            </button>
          ),
        }]
      : []),
  ];

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.ticket_number.trim()) return setError('Enter the locate ticket number.');
    if (!form.valid_from || !form.valid_to) return setError('Enter the days the locate is valid from and to.');
    if (form.valid_to < form.valid_from) return setError('The "valid to" day cannot be before the "valid from" day.');
    setBusy(true);
    try {
      const uploadId = file ? await uploadEwFile('ew_site', siteId, file) : null;
      await add.mutateAsync({
        siteId,
        body: {
          ticket_number: form.ticket_number.trim(),
          provider: blankToNull(form.provider),
          requested_on: blankToNull(form.requested_on),
          valid_from: form.valid_from,
          valid_to: form.valid_to,
          job_id: form.job_id || null,
          upload_id: uploadId,
          notes: blankToNull(form.notes),
        },
      });
      toast.success('Locate added');
      setAdding(false);
      setForm(EMPTY);
      setFile(null);
    } catch (err) {
      setError(writeError(err, 'The locate could not be added.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EwPanel
      eyebrow="Safety"
      title="Utility locates"
      testId="ew-site-locates"
      actions={
        canWrite && !adding ? (
          <button type="button" className={EW_PRIMARY} onClick={() => { setError(null); setAdding(true); }}>
            <Plus className="size-4" aria-hidden="true" />
            Add locate
          </button>
        ) : undefined
      }
    >
      {adding && canWrite && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="ew-locate-form">
          <EwFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <EwField label="Ticket #"><input className={EW_INPUT} maxLength={50} aria-label="Locate ticket number" value={form.ticket_number} onChange={(e) => setForm({ ...form, ticket_number: e.target.value })} /></EwField>
            <EwField label="Provider"><input className={EW_INPUT} maxLength={100} aria-label="Locate provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></EwField>
            <EwField label="Requested"><input type="date" className={EW_DATE_INPUT} aria-label="Requested on" value={form.requested_on} onChange={(e) => setForm({ ...form, requested_on: e.target.value })} /></EwField>
            <EwField label="For job">
              <select className={EW_INPUT} aria-label="For job" value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })}>
                <option value="">Whole site</option>
                {jobs.map((j) => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}
              </select>
            </EwField>
            <EwField label="Valid from"><input type="date" className={EW_DATE_INPUT} aria-label="Valid from" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></EwField>
            <EwField label="Valid to"><input type="date" className={EW_DATE_INPUT} aria-label="Valid to" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></EwField>
            <EwField label="Locate sheet" hint="Optional">
              <input type="file" accept="application/pdf,image/*" aria-label="Locate sheet" className="text-sm text-fieldiq-text" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            </EwField>
            <EwField label="Notes"><input className={EW_INPUT} aria-label="Locate notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></EwField>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className={EW_SECONDARY} onClick={() => setAdding(false)} disabled={busy}>Cancel</button>
            <button type="submit" className={EW_PRIMARY} disabled={busy}>{busy ? 'Saving…' : 'Add locate'}</button>
          </div>
        </form>
      )}
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={(l) => ewLocateSkin(l.status).rail}
        subject="locates"
        testId="ew-locate"
        minWidth="min-w-[880px]"
        empty={<EwEmpty>No utility locate is on file for this site. Ground disturbance warns until one is.</EwEmpty>}
        card={(l) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-mono text-sm font-extrabold text-white">{l.ticketNumber}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">{l.provider || '—'} · {ewDay(l.validFrom)} → {ewDay(l.validTo)}</p>
            </div>
            <EwBadge skin={ewLocateSkin(l.status)} value={l.status} />
          </div>
        )}
      />
      <EwDialog
        open={!!removing}
        title="Remove locate"
        onClose={() => setRemoving(null)}
        footer={
          <>
            <button type="button" className={EW_SECONDARY} onClick={() => setRemoving(null)} disabled={remove.isPending}>Keep it</button>
            <button
              type="button"
              className={EW_PRIMARY}
              disabled={remove.isPending}
              onClick={() =>
                removing &&
                remove.mutate(removing.id, {
                  onSuccess: () => { toast.success('Locate removed'); setRemoving(null); },
                  onError: (err) => setRemoveError(ewRefusal(err, 'The locate could not be removed.')),
                })
              }
            >
              {remove.isPending ? 'Removing…' : 'Remove'}
            </button>
          </>
        }
      >
        <EwFormError message={removeError} />
        <p className="text-sm text-fieldiq-text">Removes locate {removing?.ticketNumber ?? ''} from the site.</p>
      </EwDialog>
    </EwPanel>
  );
}
